/**
 * Global Presence Manager
 * 
 * This module manages user presence across all pages in the application.
 * It provides a centralized way to track active users and their avatars.
 */

import { getSupabase } from '../components/supabaseClient';
import { dbgLog } from './appDebugFlags';

const gpLog = (...args) => dbgLog('global-presence', ...args);

class GlobalPresenceManager {
  constructor() {
    this.activeUsers = new Map(); // userId -> user data
    this.subscribers = new Set(); // Components that want to be notified of changes
    this.supabase = null;
    this.presenceChannel = null;
    this.currentUser = null;
    this.isInitialized = false;
    this.heartbeatInterval = null;
    this.heartbeatIntervalMs = 5000; // 5 seconds IMPORTANT: THIS AFFECTS HOW LONG TILL USER PRESENCE IS SCANNED AND AVATAR IS DISPLAYED
    this.presenceRescanInterval = null; // safety net if join/sync callbacks are missed
    this.isUserRemoved = false; // Track if current user has been explicitly removed
    this.userColorsCache = new Map(); // Global cache of user colors (userId -> color)
    this.lastKnownPresenceContext = new Map(); // userId -> { document_path, patient_id, patient_name, schedule_date, lastSeenAt }
    this.pendingCurrentUserRemovalTimer = null; // short delay to avoid transition flicker between pages

    // Grace period tracking for disconnected users
    this.GRACE_PERIOD_MS = 10000; // 10 seconds before removing disconnected users
    this.staleUsers = new Map(); // userId -> { user data, timer, disconnectedAt }
    this.lastKnownCursors = new Map(); // userId -> { index, length, documentPath }
    /** Serializes initialize() so two callers cannot run setupPresenceChannel() in parallel (Supabase: no .on('presence') after join). */
    this._initializePromise = null;
    /** Serializes reconnect when the Realtime channel is closed/errored so track() syncs again after leave/rejoin. */
    this._presenceReconnectPromise = null;

    /** Fallback when presenceState() omits remote clients — Realtime broadcast still delivers. */
    this.broadcastPeers = new Map(); // userId -> { row, receivedAt }
    /** User IDs seen in the last Supabase presence snapshot — used to drop broadcast fallbacks when someone untracks. */
    this._lastPresenceChannelUserIds = null;
    this._BROADCAST_EVENT = 'gp_presence_ping';
    // Short TTL: broadcast is a fallback only; avoids showing users who left or never opened this page.
    this._BROADCAST_TTL_MS = 6000;
    this._presenceHealth = {
      channelState: null,
      lastPresenceSyncAt: null,
      lastPresenceJoinAt: null,
      lastPresenceLeaveAt: null,
      lastTrackSentAt: null,
      lastBroadcastSentAt: null,
      lastBroadcastReceivedAt: null,
      lastBroadcastSendError: null,
      presenceKeyCount: 0,
      presenceRowCount: 0,
      broadcastPeerCount: 0,
    };
  }

  /**
   * Cache a user's color globally
   * @param {string} userId - The user's ID
   * @param {string} color - The user's color
   */
  cacheUserColor(userId, color) {
    if (userId && color) {
      // Don't overwrite existing: keeps same avatar/cursor color when user leaves and re-enters
      if (!this.userColorsCache.has(userId)) {
        this.userColorsCache.set(userId, color);
        // Notify subscribers so components like PatientPage can immediately pick up updated colors
        this.notifySubscribers();
      }
    }
  }

  /**
   * Cache multiple user colors at once. Does not overwrite existing cached colors.
   * @param {Map|Object} colors - Map or object of userId -> color
   */
  cacheUserColors(colors) {
    let hasChanges = false;
    if (colors instanceof Map) {
      colors.forEach((color, userId) => {
        if (userId && color && !this.userColorsCache.has(userId)) {
          this.userColorsCache.set(userId, color);
          hasChanges = true;
        }
      });
    } else if (colors && typeof colors === 'object') {
      Object.entries(colors).forEach(([userId, color]) => {
        if (userId && color && !this.userColorsCache.has(userId)) {
          this.userColorsCache.set(userId, color);
          hasChanges = true;
        }
      });
    }
    // Only notify subscribers if we actually changed something
    if (hasChanges) {
      this.notifySubscribers();
    }
  }

  /**
   * Get cached color for a user
   * @param {string} userId - The user's ID
   * @returns {string|null} - The user's cached color or null
   */
  getCachedUserColor(userId) {
    return this.userColorsCache.get(userId) || null;
  }

  /**
   * Get all cached user colors as a Map
   * @returns {Map} - Map of userId -> color
   */
  getCachedUserColors() {
    return new Map(this.userColorsCache);
  }

  /**
   * Get cached colors for users in a specific document
   * @param {string} documentPath - The document path to filter by
   * @returns {Map} - Map of userId -> color for users in the document
   */
  getCachedColorsForDocument(documentPath) {
    const colorsMap = new Map();

    // Get users who are in this document
    this.activeUsers.forEach((user, userId) => {
      if (user.document_path === documentPath) {
        const color = this.userColorsCache.get(userId);
        if (color) {
          colorsMap.set(userId, color);
        }
      }
    });

    return colorsMap;
  }

  /**
   * Store last known cursor position for a user
   * @param {string} userId - The user's ID
   * @param {Object} cursorData - { index, length, documentPath }
   */
  setLastKnownCursor(userId, cursorData) {
    if (userId && cursorData && cursorData.index !== undefined) {
      this.lastKnownCursors.set(userId, {
        ...cursorData,
        timestamp: Date.now()
      });
    }
  }

  /**
   * Get last known cursor position for a user
   * @param {string} userId - The user's ID
   * @returns {Object|null} - { index, length, documentPath, timestamp } or null
   */
  getLastKnownCursor(userId) {
    return this.lastKnownCursors.get(userId) || null;
  }

  /**
   * Check if a user is in the grace period (stale but not yet removed)
   * @param {string} userId - The user's ID
   * @returns {boolean}
   */
  isUserStale(userId) {
    return this.staleUsers.has(userId);
  }

  /**
   * Get stale user data (for users in grace period)
   * @param {string} userId - The user's ID
   * @returns {Object|null} - User data if stale, null otherwise
   */
  getStaleUser(userId) {
    const staleEntry = this.staleUsers.get(userId);
    return staleEntry ? staleEntry.userData : null;
  }

  /**
   * Start grace period for a user (called when they disconnect from WebSocket)
   * @param {string} userId - The user's ID
   * @param {Object} userData - The user's data to preserve during grace period
   */
  startGracePeriod(userId, userData) {
    if (!userId) return;

    // Don't start grace period if user is already stale
    if (this.staleUsers.has(userId)) {
      gpLog(`⏳ Grace period already active for ${userData?.name || userId}`);
      return;
    }

    gpLog(`⏳ Starting ${this.GRACE_PERIOD_MS / 1000}s grace period for ${userData?.name || userId}`);

    // Store user data with stale marker
    const staleUserData = {
      ...userData,
      isStale: true,
      disconnectedAt: Date.now()
    };

    // Set up timer to remove user after grace period
    const timer = setTimeout(() => {
      this.finalizeUserRemoval(userId);
    }, this.GRACE_PERIOD_MS);

    this.staleUsers.set(userId, {
      userData: staleUserData,
      timer,
      disconnectedAt: Date.now()
    });

    // ALWAYS add/update the user in activeUsers with stale marker during grace period
    // The user may have already been removed by Supabase presence by this point,
    // so we need to re-add them to ensure they stay visible
    this.activeUsers.set(userId, staleUserData);
    this.notifySubscribers();

    gpLog(`📍 Stale user added to activeUsers:`, {
      userId,
      name: staleUserData.name,
      isStale: staleUserData.isStale,
      activeUsersCount: this.activeUsers.size,
      staleUsersCount: this.staleUsers.size
    });
  }

  /**
   * Cancel grace period for a user (called when they reconnect)
   * @param {string} userId - The user's ID
   */
  cancelGracePeriod(userId) {
    if (!userId) return;

    const staleEntry = this.staleUsers.get(userId);
    if (staleEntry) {
      gpLog(`✅ Cancelling grace period for user ${userId} - they reconnected!`);
      clearTimeout(staleEntry.timer);
      this.staleUsers.delete(userId);

      // Remove stale marker from active user
      const activeUser = this.activeUsers.get(userId);
      if (activeUser) {
        this.activeUsers.set(userId, {
          ...activeUser,
          isStale: false,
          disconnectedAt: undefined
        });
        this.notifySubscribers();
      }
    }
  }

  /**
   * Finalize user removal after grace period expires
   * @param {string} userId - The user's ID
   */
  finalizeUserRemoval(userId) {
    const staleEntry = this.staleUsers.get(userId);
    if (!staleEntry) {
      return;
    }
    const elapsed = Date.now() - staleEntry.disconnectedAt;
    // Tab throttling / timer drift: never remove before the full grace window elapses.
    if (elapsed < this.GRACE_PERIOD_MS - 50) {
      const remaining = Math.max(50, this.GRACE_PERIOD_MS - elapsed);
      gpLog(
        `⏱️ Grace finalize early (${elapsed}ms < ${this.GRACE_PERIOD_MS}ms), rescheduling ${remaining}ms for ${userId}`,
      );
      if (staleEntry.timer) {
        clearTimeout(staleEntry.timer);
      }
      staleEntry.timer = setTimeout(() => this.finalizeUserRemoval(userId), remaining);
      return;
    }

    gpLog(`🧹 Grace period expired, removing user ${userId}`);

    this.staleUsers.delete(userId);
    this.activeUsers.delete(userId);

    // Keep last known cursor for a while longer (5 minutes) in case user returns
    // Don't delete it immediately

    this.notifySubscribers();
  }

  /**
   * Initialize the global presence manager
   */
  async initialize() {
    if (this.isInitialized) {
      gpLog('🔄 Global presence manager already initialized');
      return;
    }
    if (this._initializePromise) {
      return this._initializePromise;
    }
    this._initializePromise = this._initializeInner().finally(() => {
      this._initializePromise = null;
    });
    return this._initializePromise;
  }

  async _initializeInner() {
    try {
      this.supabase = await getSupabase();
      const { data: { session } } = await this.supabase.auth.getSession();

      if (!session?.user) {
        gpLog('❌ No user session found for global presence manager');
        return;
      }

      let displayName = session.user.user_metadata?.name || session.user.email || 'Anonymous';
      try {
        const { data: userRow } = await this.supabase
          .from('users')
          .select('name')
          .eq('id', session.user.id)
          .single();
        if (userRow?.name) displayName = userRow.name;
      } catch (_) {}

      this.currentUser = {
        id: session.user.id,
        name: displayName
      };

      await this.setupPresenceChannel();
      this.startHeartbeat();
      this.isInitialized = true;

      gpLog('✅ Global presence manager initialized');
    } catch (error) {
      console.error('❌ Error initializing global presence manager:', error);
    }
  }

  /**
   * Set up the presence channel for real-time updates
   */
  _updatePresenceHealthCounts() {
    if (!this.presenceChannel) return;
    try {
      const state = this.presenceChannel.presenceState();
      this._presenceHealth.presenceKeyCount = Object.keys(state || {}).length;
      this._presenceHealth.presenceRowCount = Object.values(state || {}).reduce(
        (n, arr) => n + (Array.isArray(arr) ? arr.length : 0),
        0
      );
      this._presenceHealth.channelState = this.presenceChannel.state;
    } catch (_) {
      // ignore
    }
  }

  _onPresenceBroadcast(payload) {
    if (!payload || !payload.user_id) return;
    if (this.currentUser && payload.user_id === this.currentUser.id) return;
    const patientIdRaw = String(payload.patient_id || '').trim();
    const documentPathRaw = String(payload.document_path || '').trim();
    // Ignore stray pings with no routable context (prevents junk avatars).
    if (!patientIdRaw && !documentPathRaw) return;
    this._presenceHealth.lastBroadcastReceivedAt = Date.now();
    const documentPath = documentPathRaw;
    const patientId = patientIdRaw;
    const patientName = payload.patient_name || '';
    const scheduleDate = String(payload.schedule_date || '').trim();
    const row = {
      user_id: payload.user_id,
      name: payload.name || 'Anonymous',
      email: payload.email || 'Guest',
      is_typing: !!payload.is_typing,
      cursor_active: !!payload.cursor_active,
      last_activity: payload.last_activity || new Date().toISOString(),
      document_path: documentPath,
      patient_id: patientId,
      patient_name: patientName,
      schedule_date: scheduleDate,
      source: 'broadcast-ping',
    };
    this.broadcastPeers.set(payload.user_id, { row, receivedAt: Date.now() });
    if (documentPath || patientId || patientName || scheduleDate) {
      this.lastKnownPresenceContext.set(payload.user_id, {
        document_path: documentPath,
        patient_id: patientId,
        patient_name: patientName,
        schedule_date: scheduleDate,
        lastSeenAt: Date.now(),
      });
    }
    if (payload.color) {
      this.userColorsCache.set(payload.user_id, payload.color);
    }
    this.updateActiveUsers();
  }

  async _sendPresenceBroadcast(trackPayload) {
    if (!this.presenceChannel || this.isUserRemoved) return;
    try {
      await this.presenceChannel.send({
        type: 'broadcast',
        event: this._BROADCAST_EVENT,
        payload: { ...trackPayload, sentAt: Date.now() },
      });
      this._presenceHealth.lastBroadcastSentAt = Date.now();
      this._presenceHealth.lastBroadcastSendError = null;
    } catch (e) {
      this._presenceHealth.lastBroadcastSendError = String(e?.message || e);
    }
  }

  /**
   * Debug: channel + signal health for avatar / presence troubleshooting.
   */
  getPresenceHealth() {
    this._updatePresenceHealthCounts();
    return {
      ...this._presenceHealth,
      broadcastPeerCount: this.broadcastPeers.size,
      activeUsersCount: this.activeUsers.size,
      isUserRemoved: this.isUserRemoved,
      isInitialized: this.isInitialized,
      currentUserId: this.currentUser?.id || null,
    };
  }

  /**
   * Recreate the channel if it was closed/errored (e.g. after navigation); otherwise track() does not reach other clients.
   */
  async ensurePresenceChannelReady() {
    if (!this.isInitialized || !this.currentUser || !this.supabase) return;
    const ch = this.presenceChannel;
    if (ch?.state === 'joined') return;
    if (ch?.state === 'joining') return;

    if (this._presenceReconnectPromise) {
      await this._presenceReconnectPromise;
      return;
    }

    this._presenceReconnectPromise = (async () => {
      gpLog('🔄 Reconnecting presence channel (state was:', ch?.state ?? 'null', ')');
      try {
        await this.setupPresenceChannel();
        this._updatePresenceHealthCounts();
      } catch (e) {
        console.error('❌ ensurePresenceChannelReady failed:', e);
      } finally {
        this._presenceReconnectPromise = null;
      }
    })();

    await this._presenceReconnectPromise;
  }

  async setupPresenceChannel() {
    try {
      if (this.presenceChannel) {
        try {
          await this.presenceChannel.unsubscribe();
        } catch (e) {
          gpLog('setupPresenceChannel: previous channel unsubscribe', e?.message || e);
        }
        this.presenceChannel = null;
      }

      this.presenceChannel = this.supabase.channel('global-presence', {
        config: {
          presence: {
            key: this.currentUser.id,
          },
          broadcast: { self: true },
        },
      });

      // Handle presence changes
      this.presenceChannel
        .on('presence', { event: 'sync' }, () => {
          this._presenceHealth.lastPresenceSyncAt = Date.now();
          this._updatePresenceHealthCounts();
          this.updateActiveUsers();
        })
        .on('presence', { event: 'join' }, () => {
          this._presenceHealth.lastPresenceJoinAt = Date.now();
          this._updatePresenceHealthCounts();
          this.updateActiveUsers();
        })
        .on('presence', { event: 'leave' }, () => {
          this._presenceHealth.lastPresenceLeaveAt = Date.now();
          this._updatePresenceHealthCounts();
          this.updateActiveUsers();
        })
        .on('broadcast', { event: this._BROADCAST_EVENT }, (msg) => {
          const payload = msg?.payload ?? msg;
          this._onPresenceBroadcast(payload);
        });

      const subStatus = await this.presenceChannel.subscribe();
      this._presenceHealth.channelState = this.presenceChannel.state;
      gpLog('✅ Global presence channel subscribed', subStatus);
    } catch (error) {
      console.error('❌ Error setting up global presence channel:', error);
    }
  }

  /**
   * Update the active users list from presence data
   * IMPORTANT: This merges with existing users instead of replacing them
   * to preserve users added from websocket-awareness (document pages)
   */
  updateActiveUsers() {
    if (!this.presenceChannel) return;

    const presence = this.presenceChannel.presenceState();
    const newActiveUsersFromPresence = new Map();
    const scorePresence = (presenceEntry) => {
      const p = String(presenceEntry?.document_path || '');
      const hasDocPath = !!p;
      const hasPatientId = !!presenceEntry?.patient_id;
      const isPatientPathOnly =
        p.startsWith('patient/') && !p.includes('.json');
      const isDocJsonPath = p.endsWith('.json');
      const hasName = !!presenceEntry?.name;
      // Higher score = more useful/authoritative record for avatar routing.
      // Editable .json must beat patient-only paths so collab doc context isn't replaced.
      return (
        (hasDocPath ? 4 : 0) +
        (hasPatientId ? 3 : 0) +
        (isPatientPathOnly ? 2 : 0) +
        (isDocJsonPath ? 10 : 0) +
        (hasName ? 1 : 0)
      );
    };

    // NOTE: Grace period is ONLY started by WebSocket awareness changes in useCollaboration.js
    // when a user disconnects from a document page. Supabase presence is for general page
    // tracking and should NOT trigger grace periods (it would trigger on every page navigation).

    // Merge users from presence channel with existing users
    // Preserve users with source 'websocket-awareness' that aren't in presence channel
    Object.values(presence).forEach(presences => {
      presences.forEach(presence => {
        if (presence.user_id) {
          const existingCandidate = newActiveUsersFromPresence.get(presence.user_id);
          // If multiple records exist for same user, keep the most informative one.
          if (existingCandidate && scorePresence(existingCandidate.__rawPresence || {}) > scorePresence(presence)) {
            return;
          }

          // Get existing user to preserve their color and potentially merge document paths
          const existingUser = this.activeUsers.get(presence.user_id);
          const existingColor = existingUser ? this.userColorsCache.get(presence.user_id) : null;

          // Determine which document_path to use
          // If existing user is on patient page and new presence is on document (or vice versa),
          // we want to keep the most recent/relevant one, but ensure patient_id matches
          let documentPathToUse = presence.document_path || '';
          let patientIdToUse = presence.patient_id || '';
          let patientNameToUse = presence.patient_name || '';
          let scheduleDateToUse = String(presence.schedule_date || '').trim();
          const lastKnownContext = this.lastKnownPresenceContext.get(presence.user_id);

          // If existing user has same patient_id, we can merge their presence
          // Keep the document path from presence (most recent), but ensure patient_id is consistent
          if (existingUser && existingUser.patient_id && presence.patient_id) {
            // If patient_id matches, use the new document_path but keep patient_id consistent
            if (existingUser.patient_id === presence.patient_id) {
              patientIdToUse = existingUser.patient_id; // Keep consistent patient_id
            }
          }

          // Preserve known patient context when a weaker presence row arrives (common during joins).
          if (!documentPathToUse && existingUser?.document_path) {
            documentPathToUse = existingUser.document_path;
          } else if (!documentPathToUse && lastKnownContext?.document_path) {
            documentPathToUse = lastKnownContext.document_path;
          }
          if (!patientIdToUse && existingUser?.patient_id) {
            patientIdToUse = existingUser.patient_id;
          } else if (!patientIdToUse && lastKnownContext?.patient_id) {
            patientIdToUse = lastKnownContext.patient_id;
          }
          if (!patientNameToUse && existingUser?.patient_name) {
            patientNameToUse = existingUser.patient_name;
          } else if (!patientNameToUse && lastKnownContext?.patient_name) {
            patientNameToUse = lastKnownContext.patient_name;
          }
          if (!scheduleDateToUse && existingUser?.schedule_date) {
            scheduleDateToUse = String(existingUser.schedule_date || '').trim();
          } else if (!scheduleDateToUse && lastKnownContext?.schedule_date) {
            scheduleDateToUse = String(lastKnownContext.schedule_date || '').trim();
          }

          // Extract color from presence data (synced from other clients)
          const presenceColor = presence.color || existingColor;
          
          // Add user to new map
          newActiveUsersFromPresence.set(presence.user_id, {
            user_id: presence.user_id,
            name: presence.name || 'Anonymous',
            email: presence.email || 'Guest',
            is_typing: presence.is_typing || false,
            cursor_active: presence.cursor_active || false,
            last_activity: presence.last_activity || new Date().toISOString(),
            document_path: documentPathToUse,
            patient_id: patientIdToUse,
            patient_name: patientNameToUse,
            schedule_date: scheduleDateToUse,
            source: 'global-presence',
            __presenceConfirmed: true,
            __rawPresence: presence
          });

          // Keep latest usable context so future weak rows cannot erase it.
          if (documentPathToUse || patientIdToUse || patientNameToUse || scheduleDateToUse) {
            this.lastKnownPresenceContext.set(presence.user_id, {
              document_path: documentPathToUse || '',
              patient_id: patientIdToUse || '',
              patient_name: patientNameToUse || '',
              schedule_date: scheduleDateToUse || '',
              lastSeenAt: Date.now()
            });
          }

          // Cache color from presence (synced from other clients) or preserve existing
          if (presenceColor) {
            this.userColorsCache.set(presence.user_id, presenceColor);
            // Notify subscribers so components like PatientPage can pick up the color
            this.notifySubscribers();
          } else if (existingColor) {
            // Color is already in cache, ensure it stays
            this.userColorsCache.set(presence.user_id, existingColor);
          }
        }
      });
    });

    const currentPresenceIds = new Set(newActiveUsersFromPresence.keys());
    // If someone was in the channel snapshot before but not now, they untracked (left patient/schedules/etc.).
    // Remove their broadcast fallback immediately — otherwise avatars linger until _BROADCAST_TTL_MS.
    if (this._lastPresenceChannelUserIds) {
      for (const uid of this._lastPresenceChannelUserIds) {
        if (!currentPresenceIds.has(uid)) {
          this.broadcastPeers.delete(uid);
        }
      }
    }
    this._lastPresenceChannelUserIds = currentPresenceIds;

    // Build the new active users map
    const newActiveUsers = new Map(newActiveUsersFromPresence);

    // Remove internal helper fields before exposing to subscribers/components.
    for (const [userId, user] of newActiveUsers.entries()) {
      if (user && Object.prototype.hasOwnProperty.call(user, '__rawPresence')) {
        const { __rawPresence, ...cleanUser } = user;
        newActiveUsers.set(userId, cleanUser);
      }
    }

    // Merge richer websocket/collaboration fields when the user is still in Supabase presence.
    // If they are gone from the presence channel (untracked, e.g. navigated to /schedules), do NOT
    // re-inject websocket rows — otherwise avatars never disappear for other clients.
    // Grace-period / stale users are merged in the next block.
    for (const [userId, user] of this.activeUsers.entries()) {
      if (user.source !== 'websocket-awareness') continue;
      if (newActiveUsers.has(userId)) {
        const presenceRow = newActiveUsers.get(userId);
        newActiveUsers.set(userId, {
          ...presenceRow,
          is_typing: user.is_typing ?? presenceRow.is_typing,
          cursor_active: user.cursor_active ?? presenceRow.cursor_active,
          document_path: user.document_path || presenceRow.document_path,
          patient_id: user.patient_id || presenceRow.patient_id,
          patient_name: user.patient_name || presenceRow.patient_name,
          schedule_date: user.schedule_date || presenceRow.schedule_date || '',
          source: 'websocket-awareness',
          __presenceConfirmed: true,
        });
      }
      // If not in presence and not in stale grace (handled below), drop — user left tracking.
    }

    // Remote collaborators are in Yjs awareness + websocket rows, but this client's
    // presenceState() can omit them briefly (or entirely). Re-inject recent doc-session
    // websocket rows only — not broadcast ghosts; stale users are handled next.
    const ORPHAN_WEBSOCKET_MAX_AGE_MS = 90000;
    const orphanNow = Date.now();
    for (const [userId, user] of this.activeUsers.entries()) {
      if (user.source !== 'websocket-awareness') continue;
      if (newActiveUsers.has(userId)) continue;
      if (user.isStale === true) continue;
      if (this.currentUser && userId === this.currentUser.id) continue;
      const p = String(user.document_path || '');
      if (!p.endsWith('.json')) continue;
      const lastMs = user.last_activity ? new Date(user.last_activity).getTime() : 0;
      if (!lastMs || orphanNow - lastMs > ORPHAN_WEBSOCKET_MAX_AGE_MS) continue;
      newActiveUsers.set(userId, {
        ...user,
        __presenceConfirmed: true,
        source: 'websocket-awareness',
      });
    }

    // Keep stale users in activeUsers (with stale marker) during grace period
    // IMPORTANT: Always use the stale user data (which has the ORIGINAL document path
    // from when they went stale). This ensures they appear on the document page they
    // LEFT, not on their new page (e.g., patient page).
    // Never overwrite with stale data for the current user - we manage our own presence
    // via trackUser/removeCurrentUser; showing ourselves as stale when we navigated is wrong.
    for (const [userId, staleEntry] of this.staleUsers) {
      if (this.currentUser && userId === this.currentUser.id) continue;
      newActiveUsers.set(userId, {
        ...staleEntry.userData,
        __presenceConfirmed: true,
      });
    }

    // Broadcast fallback: some clients never see remote rows in presenceState(); pings still arrive.
    this._presenceHealth.broadcastPeerCount = this.broadcastPeers.size;
    const nowMs = Date.now();
    for (const [userId, entry] of [...this.broadcastPeers.entries()]) {
      if (nowMs - entry.receivedAt > this._BROADCAST_TTL_MS) {
        this.broadcastPeers.delete(userId);
        continue;
      }
      if (!newActiveUsers.has(userId)) {
        newActiveUsers.set(userId, {
          ...entry.row,
          __presenceConfirmed: false,
          __broadcastReceivedAt: entry.receivedAt,
        });
      }
    }

    this._updatePresenceHealthCounts();
    this.activeUsers = newActiveUsers;
    this.notifySubscribers();
    
    // gpLog('🔄 Global presence updated:', {
    //   activeUsers: this.activeUsers.size,
    //   users: Array.from(this.activeUsers.values()).map(u => u.name)
    // });
  }

  /**
   * Get active users filtered by current patient page
   */
  getActiveUsersForPatient(currentPatientId, patientName = null, scheduleDate = null) {
    if (!currentPatientId) {
      return Array.from(this.activeUsers.values());
    }

    const norm = (d) => {
      if (d == null || d === '') return '';
      const s = String(d).trim();
      return s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
    };
    const scheduleNorm = scheduleDate ? norm(scheduleDate) : '';
    const dayFromPath = (documentPath) => {
      const m = String(documentPath || '').match(/(\d{4}-\d{2}-\d{2})/);
      return m ? norm(m[1]) : '';
    };

    return Array.from(this.activeUsers.values()).filter(user => {
      // SIMPLIFIED LOGIC: If patient_id matches exactly, always include the user
      // This ensures users from Edit Doc always show on Patient page and vice versa
      const isSamePatient = user.patient_id === currentPatientId;

      // Primary check: patient_id match (most reliable)
      if (isSamePatient) {
        return true;
      }

      // Fallback checks for edge cases where patient_id might not be set correctly
      const isOnPatientPage = user.document_path === `patient/${currentPatientId}`;

      // Check if document path contains the patient ID
      const isOnDocumentPage = user.document_path?.startsWith(`${currentPatientId}/`) ||
                               user.document_path?.includes(`/${currentPatientId}/`) ||
                               user.document_path?.includes(`-${currentPatientId}.json`) ||
                               user.document_path?.includes(`-${currentPatientId} `) ||
                               user.document_path?.includes(`/${currentPatientId}.json`);

      const isSamePatientByDocumentPath = user.document_path?.includes(`-${currentPatientId}.json`) ||
                                         user.document_path?.includes(`-${currentPatientId} `) ||
                                         user.document_path?.includes(`/${currentPatientId}.json`);

      // Check if the document path contains the patient name (e.g. "Shahwan, Omar-123456.json")
      // This handles cases where patient_id is set to EHR_ID instead of UUID
      const isMatchByPatientName = patientName && patientName.length > 0 &&
                                   user.document_path?.includes(patientName);

      // Check if user is in a document (websocket-awareness) for this patient
      const isUserInDocument = user.source === 'websocket-awareness' && user.document_path?.endsWith('.json');
      const isUserForSamePatient = isUserInDocument && (isOnDocumentPage || isSamePatientByDocumentPath || isMatchByPatientName);

      // For document pages, also check if we're on the same document
      const currentUserDocumentPath = this.getCurrentUserDocumentPath();
      const isSameDocumentPath = user.document_path === currentUserDocumentPath;
      const isDocumentPage = currentUserDocumentPath && currentUserDocumentPath.includes('.json');

      // If we're on a document page, show users on same document OR same patient
      if (isDocumentPage) {
        return isSameDocumentPath || isSamePatient || isOnDocumentPage || isSamePatientByDocumentPath || isMatchByPatientName;
      }

      // For all other cases (patient page or unknown), include if any condition matches
      return isOnPatientPage || isOnDocumentPage || isSamePatientByDocumentPath || isUserForSamePatient || isMatchByPatientName;
    }).filter((user) => {
      if (!scheduleNorm) return true;
      const effectiveDay = norm(user.schedule_date) || dayFromPath(user.document_path);
      return effectiveDay === scheduleNorm;
    });
  }

  /**
   * Get the current user's document path for comparison
   */
  getCurrentUserDocumentPath() {
    if (!this.currentUser) return null;
    const currentUserState = this.activeUsers.get(this.currentUser.id);
    return currentUserState?.document_path || null;
  }

  /**
   * Start heartbeat to maintain presence
   */
  startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    if (this.presenceRescanInterval) {
      clearInterval(this.presenceRescanInterval);
    }

    this.heartbeatInterval = setInterval(async () => {
      try {
        await this.ensurePresenceChannelReady();
      } catch (e) {
        console.error('❌ Heartbeat ensurePresenceChannelReady:', e);
      }
      if (this.presenceChannel) {
        try {
          // Only track current user if they haven't been explicitly removed
          if (!this.isUserRemoved) {
            // Get current user state from the active users map
            let currentUserState = this.activeUsers.get(this.currentUser.id);
            const lastKnownContext = this.lastKnownPresenceContext.get(this.currentUser.id);
            if ((!currentUserState || !currentUserState.document_path) && lastKnownContext?.document_path) {
              // Recover local state when a transient merge pass dropped current user context.
              currentUserState = {
                user_id: this.currentUser.id,
                name: this.currentUser.name,
                email: this.currentUser.email || 'Guest',
                is_typing: false,
                cursor_active: false,
                last_activity: new Date().toISOString(),
                document_path: lastKnownContext.document_path,
                patient_id: lastKnownContext.patient_id || '',
                patient_name: lastKnownContext.patient_name || '',
                schedule_date: lastKnownContext.schedule_date || '',
                source: 'global-presence'
              };
              this.activeUsers.set(this.currentUser.id, currentUserState);
            }
            
            // Only send heartbeat if we have a valid document_path
            // This prevents the heartbeat from overriding the document_path with empty string
            if (currentUserState && currentUserState.document_path) {
              // Get user's color from cache to sync it through presence channel
              const userColor = this.userColorsCache.get(this.currentUser.id);
              const hbPayload = {
                user_id: this.currentUser.id,
                name: this.currentUser.name,
                email: this.currentUser.email || 'Guest',
                is_typing: currentUserState.is_typing || false,
                cursor_active: currentUserState.cursor_active || false,
                last_activity: new Date().toISOString(),
                document_path: currentUserState.document_path,
                patient_id: currentUserState.patient_id || '',
                patient_name: currentUserState.patient_name || '',
                schedule_date: currentUserState.schedule_date || lastKnownContext?.schedule_date || '',
                color: userColor || null,
              };
              await this.presenceChannel.track(hbPayload);
              this._presenceHealth.lastTrackSentAt = Date.now();
              await this._sendPresenceBroadcast(hbPayload);
              // Keep local merged state fresh even if a presence callback was missed.
              this.updateActiveUsers();
            } else {
              gpLog('🔄 Heartbeat skipped - no valid document_path for current user');
            }
          } else {
            gpLog('🔄 Heartbeat skipped - current user has been removed from presence');
          }
        } catch (error) {
          console.error('❌ Error in global presence heartbeat:', error);
        }
      }
    }, this.heartbeatIntervalMs);

    // Safety-net scanner: keep activeUsers in sync even if realtime join/leave events are missed.
    this.presenceRescanInterval = setInterval(() => {
      if (!this.presenceChannel) return;
      try {
        this.updateActiveUsers();
      } catch (error) {
        console.error('❌ Error in presence rescan interval:', error);
      }
    }, 1500);
  }

  /**
   * Track a user as active on a specific document/patient
   */
  async trackUser(documentPath, patientId, patientName, isTyping = false, hasCursor = false, scheduleDate = '') {
    if (!this.isInitialized || !this.currentUser) {
      gpLog('❌ trackUser: manager not initialized');
      return;
    }

    await this.ensurePresenceChannelReady();

    if (!this.presenceChannel) {
      gpLog('❌ No presence channel available for tracking');
      return;
    }

    try {
      // Cancel pending removal if user is transitioning between pages.
      if (this.pendingCurrentUserRemovalTimer) {
        clearTimeout(this.pendingCurrentUserRemovalTimer);
        this.pendingCurrentUserRemovalTimer = null;
      }

      // Reset the removed flag when tracking a user again
      this.isUserRemoved = false;
      // Cancel any grace period for current user so we don't show as stale after navigating to this page
      this.cancelGracePeriod(this.currentUser.id);
      
      // Preserve existing color if user already has one
      const existingColor = this.userColorsCache.get(this.currentUser.id);
      const scheduleDateNorm = String(scheduleDate || '').trim();

      // Update the user's state in the active users map first
      this.activeUsers.set(this.currentUser.id, {
        user_id: this.currentUser.id,
        name: this.currentUser.name,
        email: this.currentUser.email || 'Guest',
        is_typing: isTyping,
        cursor_active: hasCursor,
        last_activity: new Date().toISOString(),
        document_path: documentPath,
        patient_id: patientId,
        patient_name: patientName,
        schedule_date: scheduleDateNorm,
        source: 'global-presence'
      });
      this.lastKnownPresenceContext.set(this.currentUser.id, {
        document_path: documentPath || '',
        patient_id: patientId || '',
        patient_name: patientName || '',
        schedule_date: scheduleDateNorm,
        lastSeenAt: Date.now()
      });
      
      // Ensure color is preserved in cache
      if (existingColor) {
        this.userColorsCache.set(this.currentUser.id, existingColor);
      }

      // Always update Supabase presence channel to ensure document_path is properly set
      // Include color in presence so it syncs across clients
      const userColor = this.userColorsCache.get(this.currentUser.id);
      const trackPayload = {
        user_id: this.currentUser.id,
        name: this.currentUser.name,
        email: this.currentUser.email || 'Guest',
        is_typing: isTyping,
        cursor_active: hasCursor,
        last_activity: new Date().toISOString(),
        document_path: documentPath,
        patient_id: patientId,
        patient_name: patientName,
        schedule_date: scheduleDateNorm,
        color: userColor || null,
      };
      await this.presenceChannel.track(trackPayload);
      this._presenceHealth.lastTrackSentAt = Date.now();
      await this._sendPresenceBroadcast(trackPayload);

      // Notify subscribers immediately and force a local merge pass so "entering user"
      // acts like a signal for existing users to rescan avatar context.
      this.notifySubscribers();
      this.updateActiveUsers();
    } catch (error) {
      console.error('❌ Error tracking user in global presence:', error);
    }
  }

  /**
   * Subscribe to presence updates
   */
  subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  /**
   * Notify all subscribers of changes
   */
  notifySubscribers() {
    this.subscribers.forEach(callback => {
      try {
        callback({
          activeUsers: Array.from(this.activeUsers.values()),
          currentUser: this.currentUser
        });
      } catch (error) {
        console.error('❌ Error notifying presence subscriber:', error);
      }
    });
  }

  /**
   * Get current active users
   */
  getActiveUsers() {
    return Array.from(this.activeUsers.values());
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Remove current user from presence (but keep manager running)
   */
  removeCurrentUser() {
    if (this.currentUser && this.presenceChannel) {
      // Delay actual removal to avoid avatar disappear/reappear flicker during
      // quick transitions (patient page <-> document page).
      if (this.pendingCurrentUserRemovalTimer) {
        clearTimeout(this.pendingCurrentUserRemovalTimer);
      }

      const userId = this.currentUser.id;
      const userName = this.currentUser.name;
      gpLog('🧹 Scheduling current user removal from presence:', userName);

      this.pendingCurrentUserRemovalTimer = setTimeout(() => {
        gpLog('🧹 Removing current user from presence now:', userName);
        this.isUserRemoved = true;
        // Cancel any grace period for current user so we never show ourselves as stale
        this.cancelGracePeriod(userId);

        // Remove current user from active users
        this.activeUsers.delete(userId);

        // Untrack the user from the presence channel to actually remove them
        this.presenceChannel.untrack();

        // Notify subscribers
        this.notifySubscribers();

        this.pendingCurrentUserRemovalTimer = null;
        gpLog('✅ Current user removed from presence');
      }, 1500);
    }
  }

  /**
   * Reset the user removal flag (for when user navigates to a new page)
   */
  resetUserRemovalFlag() {
    gpLog('🔄 Resetting user removal flag');
    this.isUserRemoved = false;
  }

  /**
   * Clean up the presence manager (complete shutdown)
   */
  cleanup() {
    if (this.pendingCurrentUserRemovalTimer) {
      clearTimeout(this.pendingCurrentUserRemovalTimer);
      this.pendingCurrentUserRemovalTimer = null;
    }
    if (this.presenceRescanInterval) {
      clearInterval(this.presenceRescanInterval);
      this.presenceRescanInterval = null;
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.presenceChannel) {
      this.presenceChannel.unsubscribe();
      this.presenceChannel = null;
    }

    // Clear all stale user timers
    for (const [userId, staleEntry] of this.staleUsers) {
      if (staleEntry.timer) {
        clearTimeout(staleEntry.timer);
      }
    }
    this.staleUsers.clear();
    this.lastKnownCursors.clear();
    this.broadcastPeers.clear();

    this.activeUsers.clear();
    this.subscribers.clear();
    this.isInitialized = false;
    this._initializePromise = null;

    gpLog('🧹 Global presence manager cleaned up');
  }
}

// Create a singleton instance
const globalPresenceManager = new GlobalPresenceManager();

export default globalPresenceManager;
